import { styled } from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { format, parseISO } from "date-fns";
import {
  type ToolbarProps,
  CommercialMonitoringDataGridToolbar,
} from "./CommercialMonitoringDataGridToolbar";
import { useGridPagination } from "../hooks/useGridPagination";
import { type CommercialMonitoringDetailRow } from "../types";
import { DataGrid } from "./DataGrid";
import { type NavigateFunction } from "react-router-dom";
import { commercialMonitoringDataGridStyles } from "./commercialMonitoringDataGridStyles";
import { CommercialMonitoringActionCell } from "./CommercialMonitoringActionCell";

interface IProps {
  navigate: NavigateFunction;
  rows: CommercialMonitoringDetailRow[];
  handleClickAcquireCommercialBook: (
    row: CommercialMonitoringDetailRow
  ) => void;
}

const StyledDataGrid = styled(DataGrid)`
  ${commercialMonitoringDataGridStyles}
`;

const makeGridColDef = (
  navigate: NavigateFunction,
  handleClickAcquireCommercialBook: (row: CommercialMonitoringDetailRow) => void
): GridColDef[] => {
  return [
    {
      field: "commercialRegistrationNumber",
      headerName: "会社法人等番号",
      width: 160,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "corporateName",
      headerName: "法人名",
      width: 260,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "registrationDate",
      headerName: "登録日",
      width: 120,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value != null) return format(parseISO(value), "yyyy/MM/dd");
      },
    },
    {
      field: "remarks",
      headerName: "備考",
      width: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
      disableExport: true,
      renderCell: ({
        row,
      }: GridRenderCellParams<string, CommercialMonitoringDetailRow>) => {
        return (
          <CommercialMonitoringActionCell
            row={row}
            navigate={navigate}
            onClickAcquireCommercialBook={handleClickAcquireCommercialBook}
          />
        );
      },
    },
  ];
};

/**
 * 商業登記モニタリングの表部分（監視対象＝法人）
 * @param {IProps} props
 * @returns
 */
export const CommercialMonitoringDetailDataGrid: React.FC<IProps> = (
  props: IProps
) => {
  const { navigate, rows, handleClickAcquireCommercialBook } = props;
  const colDef = makeGridColDef(navigate, handleClickAcquireCommercialBook);
  const { page, pageSize, changePage, changePageSize } = useGridPagination();
  const rowsPerPageOptions: number[] = [25, 50, 100];

  const dataGridPaginationProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    onPageChange: changePage,
    onPageSizeChange: changePageSize,
  };

  const toolbarProps: ToolbarProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    changePage,
    changePageSize,
    allCount: rows.length,
  };

  return (
    <StyledDataGrid
      autoHeight
      columns={colDef}
      disableColumnMenu
      disableSelectionOnClick={true}
      rows={rows}
      {...dataGridPaginationProps}
      components={{
        Toolbar: CommercialMonitoringDataGridToolbar,
      }}
      componentsProps={{
        toolbar: {
          ...toolbarProps,
        },
      }}
    />
  );
};
